/**
 * App — root component rendered by the router.
 *
 * Wraps the router outlet in:
 * 1. A Sentry ErrorBoundary (captures render errors to Sentry).
 * 2. A Suspense boundary (shows a loading skeleton while lazy routes resolve).
 */
import React, { Suspense, useEffect, useCallback, useRef } from "react";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { toast, Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import {
  INSUFFICIENT_CREDITS_EVENT,
  type InsufficientCreditsDetail,
} from "@apps/payment/frontend/api/insufficient-credits-handler";
import i18n from "@shared/i18n";
import { localizedPath } from "@shared/lib/localized-path";
import { languageAgnosticPathKey } from "./lib/route-identity";
import { isChunkLoadError, reloadForChunkLoadError } from "./app-load-recovery";
import { OfflineBanner } from "./components/offline/OfflineBanner";
import { UpdateAvailableBanner } from "./components/update-available/UpdateAvailableBanner";
import { SyncWorkerRunner } from "./sync/SyncWorkerRunner";
import { useAuth } from "@/providers/AuthProvider";
import { LegalAcceptanceGate } from "@shared/legal/legal-acceptance-gate/LegalAcceptanceGate";
import { RouteFade, RoutePendingFallback } from "./router-pending";
import { ConsentBanner } from "@shared/privacy/consent-banner";
import { trackAnalyticsPageView } from "@shared/lib/native/analytics";

function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  const { t } = useTranslation("errors");
  const isRecoverableLoadError = isChunkLoadError(error);

  useEffect(() => {
    reloadForChunkLoadError(error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {isRecoverableLoadError
            ? t("pages.refreshRequired.title")
            : t("pages.generalError.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isRecoverableLoadError
            ? t("pages.refreshRequired.description")
            : error.message}
        </p>
        <button
          onClick={
            isRecoverableLoadError ? () => window.location.reload() : resetError
          }
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {isRecoverableLoadError
            ? t("pages.refreshRequired.refreshNow")
            : t("pages.generalError.tryAgain")}
        </button>
      </div>
    </div>
  );
}

function InsufficientCreditsListener() {
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<InsufficientCreditsDetail>).detail ?? {};
      const showToast = () => {
        toast.error(
          i18n.t("payment:errors.insufficientCredits", {
            defaultValue:
              "Not enough credits. You need {{required}} but have {{available}}.",
            required: detail.required ?? "?",
            available: detail.available ?? 0,
          }),
          {
            description: i18n.t("payment:errors.insufficientCreditsDescription", {
              defaultValue:
                "Top up your wallet to continue using paid AI features.",
            }),
            action: {
              label: i18n.t("payment:errors.insufficientCreditsCta", {
                defaultValue: "Top up",
              }),
              onClick: () => {
                window.location.assign(localizedPath("/pricing"));
              },
            },
          },
        );
      };
      void i18n.loadNamespaces("payment").then(showToast, showToast);
    };

    window.addEventListener(INSUFFICIENT_CREDITS_EVENT, handler);
    return () => window.removeEventListener(INSUFFICIENT_CREDITS_EVENT, handler);
  }, []);

  return null;
}

function PushReceivedListener() {
  useEffect(() => {
    const handler = (event: Event) => {
      const detail =
        (event as CustomEvent<{ title?: string; body?: string }>).detail ?? {};
      const title = detail.title ?? "";
      const body = detail.body ?? "";
      if (!title && !body) return;
      // Foreground push (native + web FCM) — surface as a toast. The title/body
      // are already localized by the backend notification template.
      toast(title || body, title && body ? { description: body } : undefined);
    };
    window.addEventListener("push:received", handler as EventListener);
    return () =>
      window.removeEventListener("push:received", handler as EventListener);
  }, []);

  return null;
}

// Keying Suspense by the matched pathname forces a clean unmount/remount of
// the route subtree on every navigation. Without it, React 18 concurrent
// rendering keeps the previous route's tree mounted under the shared Suspense
// boundary while the next lazy chunk loads — a race that the audit reproduced
// as the wrong page (Log Treatment, Stress Journal) flashing on sibling-route
// transitions like /treatments/* and /lifestyle/meditation.
//
// The key ignores the language prefix: /health-assistant and
// /fr/health-assistant are the same page, so a language switch must NOT
// remount it (the open conversation, scroll and composer draft survive).
// Different pages still get different keys. See lib/route-identity.ts.
function RoutedOutlet() {
  const pathname = useRouterState({
    select: (state) => languageAgnosticPathKey(state.location.pathname),
  });
  return (
    <Suspense key={pathname} fallback={<RoutePendingFallback />}>
      <RouteFade>
        <Outlet />
      </RouteFade>
    </Suspense>
  );
}

function AnalyticsPageViewTracker() {
  const href = useRouterState({ select: (state) => state.location.href });
  const didSkipInitialPageView = useRef(false);

  useEffect(() => {
    if (!didSkipInitialPageView.current) {
      didSkipInitialPageView.current = true;
      return;
    }
    trackAnalyticsPageView(href);
  }, [href]);

  return null;
}

function LegalGateWrapper({ children }: { children: React.ReactNode }) {
  const { status, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = useCallback(() => {
    logout();
    void navigate({ to: "/login" as unknown as "." } as never);
  }, [logout, navigate]);

  return (
    <LegalAcceptanceGate
      isAuthenticated={status === "authenticated"}
      onSignOut={handleSignOut}
    >
      {children}
    </LegalAcceptanceGate>
  );
}

/** Sonner follows OS theme by default; the app themes via the `dark` class. */
function ThemedToaster() {
  const [theme, setTheme] = React.useState<"light" | "dark">(() =>
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
      ? "dark"
      : "light",
  );
  useEffect(() => {
    const root = document.documentElement;
    const sync = () =>
      setTheme(root.classList.contains("dark") ? "dark" : "light");
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return <Toaster theme={theme} richColors closeButton />;
}

export function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
    >
      <AnalyticsPageViewTracker />
      <InsufficientCreditsListener />
      <PushReceivedListener />
      <SyncWorkerRunner />
      <OfflineBanner />
      <UpdateAvailableBanner />
      <ConsentBanner />
      <LegalGateWrapper>
        <RoutedOutlet />
      </LegalGateWrapper>
      <ThemedToaster />
    </Sentry.ErrorBoundary>
  );
}
